/**
 * Actions step (Tailwind) — one or more actions. CREDIT / CREDIT_DYNAMIC are
 * primary; UPDATE_USER_SLAB is behind an "advanced" toggle.
 */
import React, { useState } from "react";
import { MdDelete, MdAdd } from "react-icons/md";
import { FiGift, FiPercent, FiTrendingUp, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { useBuilder } from "./BuilderContext";
import { WalletPicker } from "./inputs";
import { emptyAction } from "./ruleModel";
import { Card, Chip, Field, TextInput, Select, Btn, IconBtn } from "./ui";

const errAt = (errors, path) => errors.find((e) => e.path === path && e.severity === "error")?.message;
const warnAt = (errors, path) => errors.find((e) => e.path === path && e.severity === "warning")?.message;

const ACTION_ICONS = {
  CREDIT: FiGift,
  CREDIT_DYNAMIC: FiPercent,
  UPDATE_USER_SLAB: FiTrendingUp,
};

export default function StepActions({ draft, errors, onChange }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const actions = draft.actions || [];
  const used = new Set(actions.map((a) => a.type));

  const setAction = (i, next) => onChange(actions.map((a, idx) => (idx === i ? next : a)));
  const removeAction = (i) => onChange(actions.filter((_, idx) => idx !== i));
  const addAction = (type) => onChange(actions.length >= 1 ? [emptyAction(type)] : [...actions, emptyAction(type)]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold text-gray-900">Define Rule Execution Actions</h3>
        <p className="text-xs text-gray-500">Configure what rewards, point deposits, or tier promotions trigger when conditions pass.</p>
      </div>

      {/* Action Cards List */}
      <div className="space-y-4">
        {actions.map((a, i) => (
          <ActionCard
            key={i}
            action={a}
            path={`actions[${i}]`}
            errors={errors}
            onChange={(next) => setAction(i, next)}
            onRemove={actions.length > 1 ? () => removeAction(i) : null}
          />
        ))}
      </div>

      {/* Add Action Buttons */}
      <div className="pt-2 border-t border-gray-100 space-y-3">
        {actions.length >= 1 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Only one action per rule is supported for now. Multiple action types will be enabled in a future release.
          </p>
        )}
        <div className="flex gap-2 flex-wrap items-center">
          <Btn variant="soft" disabled={used.has("CREDIT")} onClick={() => addAction("CREDIT")}>
            <MdAdd /> Give Fixed Points
          </Btn>
          <Btn variant="soft" disabled={used.has("CREDIT_DYNAMIC")} onClick={() => addAction("CREDIT_DYNAMIC")}>
            <MdAdd /> Give Dynamic Multiplier Points
          </Btn>
          <button
            type="button"
            className="text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-gray-100"
            onClick={() => setShowAdvanced((s) => !s)}
          >
            {showAdvanced ? <FiChevronUp /> : <FiChevronDown />}
            <span>{showAdvanced ? "Hide Advanced Actions" : "Advanced Actions..."}</span>
          </button>
        </div>

        {showAdvanced && (
          <div className="p-3 rounded-xl bg-gray-50 border border-gray-200/80">
            <Btn variant="soft" disabled={used.has("UPDATE_USER_SLAB")} onClick={() => addAction("UPDATE_USER_SLAB")}>
              <MdAdd /> Assign Tier / Slab Promotion (Advanced)
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function ActionCard({ action, path, errors, onChange, onRemove }) {
  const { meta } = useBuilder();
  const label = meta?.actions?.[action.type]?.label || action.type;
  const IconComponent = ACTION_ICONS[action.type] || FiGift;

  return (
    <Card className="p-5 shadow-xs border-l-4 border-l-blue-600">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-blue-50 text-blue-600">
            <IconComponent className="h-4 w-4" />
          </div>
          <span className="font-bold text-sm text-gray-900">{label}</span>
          <Chip color="blue">{action.type}</Chip>
        </div>
        {onRemove && <IconBtn onClick={onRemove} title="Remove action"><MdDelete className="h-4 w-4" /></IconBtn>}
      </div>

      {action.type === "CREDIT" && <CreditForm action={action} path={path} errors={errors} onChange={onChange} />}
      {action.type === "CREDIT_DYNAMIC" && <DynamicForm action={action} path={path} errors={errors} onChange={onChange} />}
      {action.type === "UPDATE_USER_SLAB" && <SlabForm action={action} path={path} errors={errors} onChange={onChange} />}
    </Card>
  );
}

function CreditForm({ action, path, errors, onChange }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Point Amount" error={errAt(errors, `${path}.amount`)}>
        <TextInput
          type="number"
          placeholder="e.g. 100"
          value={action.amount ?? ""}
          onChange={(e) => onChange({ ...action, amount: e.target.value })}
          error={errAt(errors, `${path}.amount`)}
        />
      </Field>
      <Field label="Target Wallet" error={errAt(errors, `${path}.wallet_type`)} hint={warnAt(errors, `${path}.wallet_type`)}>
        <WalletPicker value={action.wallet_type} onChange={(v) => onChange({ ...action, wallet_type: v })} error={errAt(errors, `${path}.wallet_type`)} />
      </Field>
      <Field label="Ledger Description" error={errAt(errors, `${path}.description`)} hint="Description printed on user's points ledger statement" className="md:col-span-2">
        <TextInput
          placeholder="e.g. Welcome bonus points credit"
          value={action.description ?? ""}
          onChange={(e) => onChange({ ...action, description: e.target.value })}
          error={errAt(errors, `${path}.description`)}
        />
      </Field>
    </div>
  );
}

function DynamicForm({ action, path, errors, onChange }) {
  const { meta, eventType } = useBuilder();
  const baseFields = meta?.event_types?.find((e) => e.name === eventType)?.credit_base_fields || [];
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Base Field" error={errAt(errors, `${path}.base_field`)} hint="Numeric event attribute to multiply">
        <Select
          value={action.base_field || ""}
          onChange={(v) => onChange({ ...action, base_field: v })}
          placeholder="Select event base field"
          error={errAt(errors, `${path}.base_field`)}
          options={baseFields.map((f) => ({ value: f, label: meta.fact_check_keys[f]?.label || f }))}
        />
      </Field>
      <Field label="Multiplier Factor" error={errAt(errors, `${path}.multiplier`)} hint="e.g. 0.02 = 2%, 1.5 = 150%">
        <TextInput
          type="number"
          placeholder="e.g. 0.05"
          value={action.multiplier ?? ""}
          onChange={(e) => onChange({ ...action, multiplier: e.target.value })}
          error={errAt(errors, `${path}.multiplier`)}
        />
      </Field>
      <Field label="Target Wallet" error={errAt(errors, `${path}.wallet_type`)} hint={warnAt(errors, `${path}.wallet_type`)}>
        <WalletPicker value={action.wallet_type} onChange={(v) => onChange({ ...action, wallet_type: v })} error={errAt(errors, `${path}.wallet_type`)} />
      </Field>
      <Field label="Ledger Description" error={errAt(errors, `${path}.description`)} className="md:col-span-1">
        <TextInput
          placeholder="e.g. Dynamic percentage cashback"
          value={action.description ?? ""}
          onChange={(e) => onChange({ ...action, description: e.target.value })}
          error={errAt(errors, `${path}.description`)}
        />
      </Field>
    </div>
  );
}

function SlabForm({ action, path, errors, onChange }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Outcome Group Key" error={errAt(errors, `${path}.outcome_key`)} hint="Shared outcome identifier across a slab set">
        <TextInput
          placeholder="e.g. loyalty_tier"
          value={action.outcome_key ?? ""}
          onChange={(e) => onChange({ ...action, outcome_key: e.target.value })}
          error={errAt(errors, `${path}.outcome_key`)}
        />
      </Field>
      <Field label="Slab Name" error={errAt(errors, `${path}.slab_name`)}>
        <TextInput
          placeholder="e.g. Gold Tier"
          value={action.slab_name ?? ""}
          onChange={(e) => onChange({ ...action, slab_name: e.target.value })}
          error={errAt(errors, `${path}.slab_name`)}
        />
      </Field>
      <Field label="Slab Rank" error={errAt(errors, `${path}.slab_rank`)}>
        <TextInput
          type="number"
          placeholder="e.g. 2"
          value={action.slab_rank ?? ""}
          onChange={(e) => onChange({ ...action, slab_rank: e.target.value })}
          error={errAt(errors, `${path}.slab_rank`)}
        />
      </Field>
      <Field label="Description" error={errAt(errors, `${path}.description`)}>
        <TextInput
          placeholder="e.g. Upgraded to Gold tier"
          value={action.description ?? ""}
          onChange={(e) => onChange({ ...action, description: e.target.value })}
          error={errAt(errors, `${path}.description`)}
        />
      </Field>
    </div>
  );
}

